import type { Editor } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

/**
 * Keyboard shortcut that prevents backspace from doing anything at the
 * start of the given node.
 *
 * Use this to override the default behavior for titles,
 * which is selectBackwards (invisibly selecting the previous node).
 */
export function cancelBackspaceAtNodeStart(editor: Editor, nodeName: string): boolean {
  const { selection } = editor.state
  if (selection instanceof TextSelection && selection.empty) {
    const { $from } = selection
    if ($from.parent.type.name === nodeName && $from.parentOffset === 0) {
      return true
    }
  }

  return false
}

/**
 * Keyboard shortcut that prevents backspace from doing anything at the
 * start of the given node's "content", defined as its 2nd+ child nodes
 * (skipping the first node, which is assumed to be a title).
 *
 * Use this to override the default behavior, which is either selectBackwards
 * (invisibly selecting the title node) or merging the first content block into the title
 * (which happens when there are > 1 content blocks).
 *
 * List items' backspace behavior (convert to paragraph) is unaffected.
 */
export function cancelBackspaceAtContentStart(editor: Editor, nodeName: string): boolean {
  const { selection } = editor.state
  if (selection instanceof TextSelection && selection.empty) {
    const { $from } = selection
    if ($from.parentOffset === 0) {
      const grandParent = $from.node($from.depth - 1)
      if (grandParent?.type.name === nodeName) {
        const indexInGrandparent = $from.index($from.depth - 1)
        if (indexInGrandparent === 1) {
          // Selection is a text cursor at the start of nodeName's 2nd node.
          return true
        }
      }
    }
  }
  return false
}
